/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import CampaignModel from 'cio/models/campaign';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import { FREQUENCY_OPTIONS, LEAD_DAYS_TYPES, FrequencyOption, LeadDaysType } from 'cio/utils/campaigns/date-triggered';
import { EMAIL_AND_ID_IDENTIFIER_CONFIG, isAttributeEnabledIdentifier } from 'cio/utils/identifiers';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';

interface CampaignsTriggerSetupDateArgs {
  campaign: CampaignModel;
  dateAttribute: string;
  frequency: FrequencyOption;
  fallbackZone: string;
  filters: Record<string, unknown>[];
  hasUnsavedAttributes: boolean;
  isCampaignOverview: boolean;
  leadDays: number;
  maxLeadDays: number;
  startTime: string;
  zone: string;
  updateFrequency: (frequency: FrequencyOption) => void;
  updateLeadDays: (days: number) => void;
  updateStartTime: (time: string) => void;
  updateZone: (zone: string) => void;
  updateFallbackZone: (zone: string) => void;
}

export default class CampaignsTriggerSetupDate extends Component<CampaignsTriggerSetupDateArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  readonly frequencyOptions = FREQUENCY_OPTIONS;
  readonly leadDaysTypeOptions = LEAD_DAYS_TYPES;
  readonly frequencyOption = FrequencyOption;
  readonly leadDaysType = LeadDaysType;
  @tracked days = 1;
  @tracked selectedLeadDaysType: LeadDaysType = LeadDaysType.on;

  get availableAttributes() {
    return this.environment.sentAttributes.filter(
      (attr) => !isAttributeEnabledIdentifier(attr, EMAIL_AND_ID_IDENTIFIER_CONFIG) && attr !== CREATED_IN_CIO_AT,
    );
  }

  get maxleadDaysExceededTooltipContent() {
    return `A ${this.args.frequency} campaign cannot be triggered more than ${this.args.maxLeadDays} days before or after the trigger date`;
  }

  constructor(owner: unknown, args: CampaignsTriggerSetupDateArgs) {
    super(owner, args);

    if (this.args.leadDays) {
      this.setInternalLeadDays();
    }
  }

  @action
  onChangeDays(value: string) {
    let days = Number(value);
    if (days < 1) {
      days = 1;
    }

    this.days = days;
    this.setCampaignLeadDays();
  }

  @action
  onSelectLeadDaysType(type: LeadDaysType) {
    this.selectedLeadDaysType = type;
    this.setCampaignLeadDays();
  }

  setCampaignLeadDays() {
    switch (this.selectedLeadDaysType) {
      case LeadDaysType.after:
        return this.args.updateLeadDays(this.days);
      case LeadDaysType.before:
        return this.args.updateLeadDays(0 - this.days);
      case LeadDaysType.on:
        return this.args.updateLeadDays(0);
    }
  }

  setInternalLeadDays() {
    this.days = Math.abs(Number(this.args.leadDays));
    this.selectedLeadDaysType = this.args.leadDays > 0 ? LeadDaysType.after : LeadDaysType.before;
  }
}
